import type { API } from "@/api/api";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import type {
  MissingReloadStopInfo,
  MissingStopsByRoute,
  Trip,
  TripOrder,
  TripOrderStop,
  TripReloadStop,
  TripRoute,
  TripRouteFreight,
  TripRouteStop,
  TripStop,
} from "@/views/trips/edit/trip";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { RouteStatus, TravelOrderCancellationStatusMap, TravelOrderStatus } from "@/data/trip";
import type { RouteStopFacilityDto, TravelOrderDto, TravelOrderOrderInfo } from "@/api/trips/dto/trip.dto";
import { OrderFreightState, OrderStopState, OrderStopStatus, OrderStopType } from "@/data/order";
import { customAlphabet } from "nanoid";
import type { OrderFacilityDto, OrderFreightDto } from "@/api/orders/dto/order.dto";
import { RouteRedispatchState, RouteStopState } from "@/enums/trip";
import { useOrder } from "@/composables";
import { isSameEntity } from "@/utils/sameEntity";

const { isReloadStop, getFreightName, createTOName } = useOrder();

export class TripManager {
  trip: Trip = { orders: [], routes: [] };
  private api: API;
  private route: RouteLocationNormalizedLoaded;
  private newTripSlug = "new";

  constructor(api: API, route: RouteLocationNormalizedLoaded) {
    this.api = api;
    this.route = route;
  }

  get orders(): TripOrder[] {
    return this.trip.orders;
  }

  get routes(): TripRoute[] {
    return this.trip.routes;
  }

  get tripStops(): TripOrderStop[] {
    return this.orders.flatMap((o) => o.tripStops).sort((a, b) => (a.position || 0) - (b.position || 0));
  }

  get routeStops(): TripRouteStop[] {
    return this.routes.flatMap((route) => route.routeStops);
  }

  get routeFreights(): TripRouteFreight[] {
    return this.trip.routes.flatMap((r) => r.freights);
  }

  get shouldShowReorderStopsDialog(): boolean {
    const routesStops = this.trip.routes.flatMap((r) => r.routeStops);
    return (
      this.orders.length > 1 &&
      this.tripStops.findIndex(
        (os) => routesStops.findIndex((rs) => rs.orderStopId === os.id) === -1 && !os.isReload,
      ) !== -1
    );
  }

  get travelOrdersByOrder(): Record<string, TravelOrderDto[]> {
    const travelOrders = this.trip.routes.flatMap((route) => route.travelOrders);
    return travelOrders.reduce((acc: Record<string, TravelOrderDto[]>, travelOrder) => {
      if (!acc[travelOrder.orderId]) {
        acc[travelOrder.orderId] = [travelOrder];
      } else {
        acc[travelOrder.orderId].push(travelOrder);
      }

      return acc;
    }, {});
  }

  get orderFreights(): OrderFreightDto[] {
    return this.orders.flatMap((order) => order.freights);
  }

  private get isNewTrip(): boolean {
    return this.route.params.id === this.newTripSlug;
  }

  async load(): Promise<void> {
    if (this.isNewTrip) {
      return this.loadNewTrip();
    }
    return this.loadExistingTrip();
  }

  async loadTrip(tripId: number) {
    const resp = await this.api.trips.getTripById(tripId);
    if (!resp.success) {
      throw new Error("Trip not found");
    }
    const trip = resp.data!;
    const routes: TripRoute[] = trip.routes.map((r, routeIndex) => ({
      ...r,
      isDispatched: false,
      routeStops: r.routeStops.map((rs) => ({
        ...rs,
        use: true,
        name: "",
        number: 0,
        editable: true,
      })),
      travelOrders: r.travelOrders.map((to) => {
        return {
          ...to,
          position: routeIndex + 1,
          name: createTOName(routeIndex, to.order.number),
          tempId: this.generateId(),
        };
      }),
    }));

    // collect orders
    this.trip.orders = [];
    const orders: Record<string, TravelOrderOrderInfo> = {};
    for (const route of routes) {
      // route dispatched because it was saved
      route.isDispatched = route.redispatch !== RouteRedispatchState.needed;

      // load truck
      if (route.truckId) {
        const resp = await this.api.trucks.findTruckById(route!.truckId);
        if (resp.success) {
          route.truck = resp.data;
        } else {
          throw new Error("Truck not found");
        }
      }

      // load orders & update travel orders
      for (const travelOrder of route.travelOrders) {
        if (orders[travelOrder.orderId] === undefined) {
          const orderResponse = await this.api.orders.findOrderById(travelOrder.orderId);
          if (orderResponse.success && orderResponse.data) {
            const { orderStops, ...order } = orderResponse.data;
            orders[order.id!] = orderResponse.data;
            // add order only once
            this.addOrder({
              ...order,
              files: [],
              number: order.number || 1,
              freights: order.freights.map((f, i) => ({ ...f, number: i + 1 })),
              tripStops: orderStops,
            });
          }
        }

        travelOrder.order = orders[travelOrder.orderId];
      }
    }

    // collect facilities from order stops and add unused stops
    const facilities: Record<number, OrderFacilityDto> = {};
    for (const order of this.trip.orders) {
      for (const orderStop of order.tripStops) {
        facilities[orderStop.facilityId] = orderStop.facility!;

        if (orderStop.oldFacilityId) {
          if (!facilities[orderStop!.oldFacilityId]) {
            const res = await this.api.company.findFacilityById(orderStop!.oldFacilityId);
            if (res.success) {
              facilities[orderStop!.oldFacilityId] = {
                address: res.data!.address!,
                addressCoordinates: res.data!.addressCoordinates!,
                addressLine: res.data!.addressLine!,
                preciseCoordinates: res.data!.preciseCoordinates!,
                timezone: "",
                ...res.data,
                id: res.data!.id!,
                name: res.data!.name!,
                type: res.data!.type!,
              };
            }
          }
          orderStop.oldFacility = facilities[orderStop!.oldFacilityId];
        }
      }
    }

    // update facilities in route stops
    for (const route of routes) {
      for (let i = 0; i < route.routeStops.length; i++) {
        const stop = route.routeStops[i];
        stop.use = true;
        stop.editable = true;

        // add reload stop to trip stops
        if (stop.isReload && stop.type === OrderStopType.pickup) {
          /*const reloadStop = {
            type: OrderStopType.reload,
            isReload: true,
            facility: stop.facility,
            facilityId: stop.facilityId,
          };
          this.tripStops.splice(stop.position - 1, 0, reloadStop);*/
        }

        const orderStop = this.trip.orders.flatMap((o) => o.tripStops).find((os) => os.id === stop.orderStopId);
        const travelOrder = route.travelOrders.find((to) => to.orderId === stop.order?.id);
        if (travelOrder && this.isTravelOrderCancelled(travelOrder) && orderStop?.oldFacilityId) {
          stop.facilityId = orderStop!.oldFacilityId!;
          stop.facility = orderStop!.oldFacility!;
        } else if (orderStop?.state === OrderStopState.updated) {
          stop.facility = orderStop.facility;
          stop.oldFacilityId = stop.facilityId;
          stop.facilityId = stop.facility!.id;
        } else if (facilities[stop.facilityId]) {
          stop.facility = facilities[stop.facilityId];
        }

        if (orderStop?.isDeleted) {
          stop.isDeleted = true;
        }
      }
    }
    this.setReloadStopId(routes);

    this.trip.routes = routes;
    // Add route stops for new order stops if needed.
    for (const orderStop of this.tripStops) {
      const relatedRouteStop = this.routeStops.find((rs) => rs.orderStopId === orderStop.id);
      if (!relatedRouteStop) {
        for (const route of this.routes) {
          const stop = this.buildRouteStopFromOrderStop(orderStop, false, this.routes.length === 1);

          this.updateRouteStopsPositionAndNumber(route, stop);

          route.routeStops.push(stop);
        }
      }
    }

    this.sortOrderStops(
      this.tripStops,
      this.trip.routes.flatMap((r) => r.routeStops),
    );

    this.createTripReloadStopBasedOnRouteStops();
    this.addEmptyRouteStops();
    this.numerateReloadStops();
    this.numerateStandardRouteStop();
    this.setTravelOrderIdsForRouteStops();
    this.setInitialRouteFreightDeleted();
  }

  setReloadStopId(routes: TripRoute[]) {
    const groupedByPosition: Record<number, TripRouteStop[]> = this.getReloadRouteStopsByPosition(routes);

    const reloadStopIdByPosition: Record<number, string> = {};

    // 2. add the same reload stop id
    for (const [positionStr, stops] of Object.entries(groupedByPosition)) {
      const position = Number(positionStr);
      const reloadStopId = this.generateId();
      reloadStopIdByPosition[position] = reloadStopId;

      for (const stop of stops) {
        stop.reloadStopId = reloadStopId;
      }
    }

    // 3. find related pick up reload route stop and set the same reload stop id
    for (const route of routes) {
      for (const stop of route.routeStops) {
        if (stop.isReload && stop.type === OrderStopType.pickup) {
          const previousPosition = stop.position - 1;
          const matchedReloadId = reloadStopIdByPosition[previousPosition];
          if (matchedReloadId) {
            stop.reloadStopId = matchedReloadId;
          }
        }
      }
    }
  }

  addOrder(order: TripOrder): void {
    delete order.polyline;
    this.trip.orders.push({ ...order, files: [] });

    // add travel orders to routes
    this.trip.routes.forEach((route, index) => {
      route.travelOrders.push(this.getTravelOrderFromOrder(order, index));
    });

    // add order stops
    for (const orderStop of order.tripStops) {
      if (!isReloadStop(orderStop)) {
        orderStop.order = {
          id: order!.id!,
          number: order!.number!,
          status: order!.status!,
          rate: order?.rate || 0,
        };
      }

      // add order stop as route stop for all routes
      for (const route of this.trip.routes) {
        if (this.trip.routes.length === 1) {
          for (const orderFreight of order.freights) {
            const routeFreight = route.freights.find((f) => f.freightId === orderFreight.id);
            if (!routeFreight) {
              route.freights.push({
                id: orderFreight.id!,
                freightId: orderFreight.id!,
                plannedWeight: orderFreight.weight,
                plannedQuantity: orderFreight.quantity,
                length: orderFreight.length,
                width: orderFreight.width,
                height: orderFreight.height,
                stackable: orderFreight.stackable,
                type: orderFreight.type!,
                isDeleted: orderFreight.isDeleted,
                $isNew: true,
                state: orderFreight.state || OrderFreightState.normal,
              });
            } else {
              routeFreight.isDeleted = orderFreight.isDeleted;
            }
          }
        }

        const routeStop = this.buildRouteStopFromOrderStop(orderStop, this.trip.routes.length === 1);
        route.routeStops.push(routeStop);
        this.recalculateRouteStops(route);
        this.setTravelOrderIdsForRouteStop(route, routeStop);
      }
    }

    this.numerateOrderStops();
  }

  createRouteFreightFromOrderFreight(
    orderFreight: OrderFreightDto,
    index: number,
    quantity: number | null = null,
    weight: number | null = null,
  ): TripRouteFreight {
    return {
      id: this.generateId(),
      freightId: orderFreight.id!,
      type: orderFreight.type!,
      plannedWeight: weight || orderFreight.weight,
      plannedQuantity: quantity || orderFreight.quantity,
      length: orderFreight.length,
      width: orderFreight.width,
      height: orderFreight.height,
      stackable: orderFreight.stackable,
      number: index + 1,
      isDeleted: orderFreight.isDeleted,
      $isNew: true,
      state: orderFreight.state || OrderFreightState.normal,
    };
  }

  createRouteFreights(route: TripRoute): TripRouteFreight[] {
    const deliveries = route.routeStops.filter((routeStop) => routeStop.type === OrderStopType.delivery);
    const hasOneOrderFreight = this.orderFreights.length === 1;

    if (hasOneOrderFreight) {
      return this.orderFreights.flatMap((orderFreight) => {
        const dividedWeight = orderFreight.weight ? Math.floor(orderFreight.weight / deliveries.length) : null;
        const dividedQuantity = orderFreight.quantity ? Math.floor(orderFreight.quantity / deliveries.length) : null;

        return deliveries.map((_, index) => {
          return this.createRouteFreightFromOrderFreight(orderFreight, index, dividedQuantity, dividedWeight);
        });
      });
    }

    return this.orderFreights.map((orderFreight, index) => {
      return this.createRouteFreightFromOrderFreight(orderFreight, index);
    });
  }

  setRouteStopFreights(
    routeStop: TripRouteStop,
    routeStops: TripRouteStop[],
    routeFreights: TripRouteFreight[],
  ): string[] {
    const relatedRouteFreights = routeFreights.filter((routeFreight) =>
      routeStop.freights.includes(routeFreight.freightId),
    );

    if (routeStop.type === OrderStopType.pickup || this.orderFreights.length > 1) {
      return relatedRouteFreights.map((freight) => freight.id);
    }

    const deliveryIndex = routeStops
      .filter((routeStop) => routeStop.type === OrderStopType.delivery)
      .findIndex((delivery) => isSameEntity(delivery, routeStop));

    if (deliveryIndex === -1) {
      return [];
    }

    return [relatedRouteFreights[deliveryIndex].id];
  }

  addRoute(truck: TruckDto | null = null, copyFreights = true) {
    const route: TripRoute = {
      distances: {},
      emptyMiles: null,
      freights: [],
      redispatch: "",
      status: RouteStatus.notStarted,
      totalDistance: 0,
      truck: truck,
      truckId: truck?.id,
      travelOrders: [],
      routeStops: [],
      tempId: this.generateId(),
      isDispatched: false,
    };

    let pickUpNumber = 1,
      deliveryNumber = 1;

    for (const orderStop of this.tripStops) {
      const stop = this.buildRouteStopFromOrderStop(
        orderStop,
        copyFreights,
        !this.routes.length,
        OrderStopStatus.notStarted,
      );

      if (stop.type === OrderStopType.pickup) {
        stop.number = pickUpNumber++;
      } else if (stop.type === OrderStopType.delivery) {
        stop.number = deliveryNumber++;
      }
      if (stop.type === OrderStopType.reload) {
        const deliveryReloadRouteStop = {
          ...stop,
          type: OrderStopType.delivery,
          isReload: true,
          tempId: this.generateId(),
        };
        this.generateStopName(deliveryReloadRouteStop);
        route.routeStops.push(deliveryReloadRouteStop);

        const pickupReloadRouteStop = {
          ...stop,
          type: OrderStopType.pickup,
          isReload: true,
          tempId: this.generateId(),
        };
        this.generateStopName(pickupReloadRouteStop);
        route.routeStops.push(pickupReloadRouteStop);
      } else {
        route.routeStops.push(stop);
      }
    }

    for (const order of this.trip.orders) {
      route.travelOrders.push(this.getTravelOrderFromOrder(order, this.trip.routes.length));
    }

    route.routeStops = route.routeStops.map((routeStop, index) => {
      return {
        ...routeStop,
        position: index + 1,
      };
    });

    if (copyFreights) {
      const routeFreights = this.createRouteFreights(route);
      route.freights = routeFreights;
      route.routeStops = route.routeStops.map((routeStop) => {
        return {
          ...routeStop,
          freights: this.setRouteStopFreights(routeStop, route.routeStops, routeFreights),
        };
      });
    }

    this.trip.routes.push(route);
  }

  updateFreights(updatedRoute: TripRoute, updatedFreights: TripRouteFreight[], updatedRouteStop: TripRouteStop) {
    const updateFreightsById = new Map(updatedFreights.map((f) => [f.id, f]));
    const updatedTrip = {
      ...this.trip,
      routes: this.trip.routes.map((route) => {
        if (!isSameEntity(route, updatedRoute)) {
          return route;
        }

        const merged = route.freights
          .filter((freight) => {
            return updateFreightsById.has(freight.id);
          })
          .map((f) => (updateFreightsById.has(f.id) ? { ...f, ...updateFreightsById.get(f.id)! } : f));

        const newOnes = updatedFreights.filter((f) => !route.freights.some((rf) => rf.id === f.id));
        const updatedRouteStops = route.routeStops.map((routeStop) => {
          return {
            ...routeStop,
            freights: routeStop.freights.filter((freight) => updateFreightsById.has(freight)),
          };
        });

        return { ...route, freights: [...merged, ...newOnes], routeStops: updatedRouteStops };
      }),
      orders: this.trip.orders.map((order) => {
        return {
          ...order,
          tripStops: order.tripStops.map((tripStop) => {
            if (!isReloadStop(tripStop)) {
              return tripStop;
            }

            const updatedReloadFreights = tripStop.reloadFreights.map((reloadFreight) => {
              const matchedFreight = updateFreightsById.get(reloadFreight.id);

              if (!matchedFreight) {
                return reloadFreight;
              }

              return {
                ...matchedFreight,
                number: reloadFreight.number,
                name: getFreightName(matchedFreight, reloadFreight.number, "", false, true),
              };
            });

            return {
              ...tripStop,
              reloadFreights: updatedReloadFreights,
            };
          }),
        };
      }),
    };

    const deletedIds = updatedTrip.routes
      .flatMap((route) => route.freights)
      .filter((f) => f.isDeleted)
      .map((f) => f.id);

    this.trip = {
      ...updatedTrip,
      routes: updatedTrip.routes.map((route) => {
        return {
          ...route,
          freights: route.freights.map((freight) => {
            let shouldDelete = freight.isDeleted;

            if (freight.parentFreightId && deletedIds.includes(freight.parentFreightId)) {
              shouldDelete = true;
            }

            const hasDeletedPickUp = updatedRouteStop.type === OrderStopType.pickup && updatedRouteStop.isDeleted;
            const hasMatchingFreight = route.freights.some((f) => updatedRouteStop.freights?.includes(f.id));
            const activePickups = route.routeStops.filter(
              (stop) => stop.type === OrderStopType.pickup && !stop.isDeleted,
            );

            if (hasMatchingFreight && hasDeletedPickUp && !activePickups.length) {
              shouldDelete = true;
            }

            return {
              ...freight,
              isDeleted: shouldDelete,
            };
          }),
        };
      }),
    };
  }

  editTravelOrder(travelOrder: TravelOrderDto) {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        travelOrders: route.travelOrders.map((to) => {
          return {
            ...to,
            order: {
              ...to.order,
              status: travelOrder.order.status,
              rate: travelOrder.order.rate,
            },
          };
        }),
      };
    });
  }

  editOrderByTravelOrder(travelOrder: TravelOrderDto) {
    const order = this.orders.find((order) => order.id === travelOrder.orderId);

    if (order) {
      order.rate = travelOrder.order.rate;
      order.status = travelOrder.order.status;
    }
  }

  copyStopPositionsFromOrder() {
    for (const route of this.trip.routes) {
      for (const stop of route.routeStops) {
        const orderStop = this.tripStops.find((os) => os.id === stop.orderStopId);
        if (orderStop) {
          stop.position = orderStop.position!;
        }
      }
    }
  }

  addReloadStop(reloadStop: TripReloadStop, prevOrderStopId: string) {
    const order = this.orders.find((o) => {
      return o.tripStops.some((tripStop) => tripStop.id === prevOrderStopId);
    });

    if (!order) {
      return;
    }

    this.updateTripStopsPositionAndNumber(reloadStop);
    order.tripStops.push(reloadStop);

    this.routes.forEach((route) => {
      const routeDeliveryStop = this.buildRouteStopFromOrderStop(
        {
          ...reloadStop,
          type: OrderStopType.delivery,
        },
        false,
        false,
      );
      const routePickupStop = this.buildRouteStopFromOrderStop(
        {
          ...reloadStop,
          type: OrderStopType.pickup,
        },
        false,
        false,
      );

      this.updateRouteStopsPositionAndNumber(route, routePickupStop);
      route.routeStops.push(routePickupStop);
      this.updateRouteStopsPositionAndNumber(route, routeDeliveryStop);
      route.routeStops.push(routeDeliveryStop);
    });
  }

  updateRouteStatus(updatedRoute: TripRoute) {
    this.trip.routes = this.trip.routes.map((route) => {
      if (updatedRoute.id === route.id) {
        const isAllTOCancelled = updatedRoute.travelOrders.every((to) =>
          TravelOrderCancellationStatusMap.find((cancelTO) => cancelTO.id === to.status),
        );

        return {
          ...route,
          status: isAllTOCancelled ? RouteStatus.cancelled : route.status,
        };
      }

      return route;
    });
  }

  updateRouteStop(updatedRouteStop: TripRouteStop) {
    this.trip = {
      ...this.trip,
      routes: this.trip.routes.map((route) => {
        return {
          ...route,
          routeStops: route.routeStops.map((routeStop) => {
            const hasMatch = isSameEntity(updatedRouteStop, routeStop);

            if (hasMatch) {
              return updatedRouteStop;
            }

            return routeStop;
          }),
        };
      }),
    };
  }

  addReloadFreights(routeStop: TripRouteStop) {
    if (!routeStop.isReload || !routeStop.freights?.length || routeStop.type !== OrderStopType.delivery) {
      return;
    }

    const selectedRouteFreights: TripRouteFreight[] = this.routeFreights.filter((routeFreight) =>
      routeStop.freights!.includes(routeFreight.id),
    );

    if (!selectedRouteFreights.length) {
      return;
    }

    this.trip = {
      ...this.trip,
      orders: this.trip.orders.map((order) => {
        return {
          ...order,
          tripStops: order.tripStops.map((orderStop) => {
            if (orderStop.id === routeStop.reloadStopId && isReloadStop(orderStop)) {
              const existingFreights = orderStop.reloadFreights || [];
              const currentLength = orderStop.reloadFreights?.length || 0;
              const existingParentIds = new Set(existingFreights.map((f) => f.id));

              const newReloadFreights = selectedRouteFreights
                .filter((freight) => !existingParentIds.has(freight.id))
                .map((selectedRouteFreight, index) => {
                  const number = currentLength + index + 1;
                  return {
                    ...selectedRouteFreight,
                    number,
                    name: getFreightName(selectedRouteFreight, number, "", false, true),
                  };
                });

              return {
                ...orderStop,
                reloadFreights: [...existingFreights, ...newReloadFreights],
              };
            }

            return orderStop;
          }),
        };
      }),
    };
  }

  preparedTripForAPI(routes: TripRoute[]) {
    return routes.map((route) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tempId, travelOrders, ...routeInfo } = route;

      const preparedTravelOrders = travelOrders.map((to) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { tempId, ...toInfo } = to;
        return {
          ...toInfo,
        };
      });

      return {
        ...routeInfo,
        travelOrders: preparedTravelOrders,
        routeStops: route.routeStops
          .filter((routeStop) => {
            if (routeStop.id) {
              return routeStop.use;
            }

            return !routeStop.isDeleted && routeStop.use;
          })
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .map(({ tempId, reloadStopId, use, ...rest }) => rest)
          .sort((a, b) => a.position - b.position),
      };
    });
  }

  getUnusedFreights(route: TripRoute): TripRouteFreight[] {
    return route.freights.filter((f) => {
      return !route.routeStops
        .filter((rs) => rs.type === OrderStopType.delivery && rs.use)
        .flatMap((s) => {
          return s.freights;
        })
        .includes(f.id);
    });
  }

  getReloadRouteStopsByPosition(routes: TripRoute[]): Record<number, TripRouteStop[]> {
    const groupedByPosition: Record<number, TripRouteStop[]> = {};
    // 1. Group delivery reload stop by position
    for (const route of routes) {
      for (const stop of route.routeStops) {
        if (stop.isReload && stop.type === OrderStopType.delivery) {
          if (!groupedByPosition[stop.position]) {
            groupedByPosition[stop.position] = [];
          }
          groupedByPosition[stop.position].push(stop);
        }
      }
    }

    return groupedByPosition;
  }

  setInitialRouteFreightDeleted() {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        freights: route.freights.map((routeFreight) => {
          const relatedOrderFreight = this.orderFreights.find(
            (orderFreight) => orderFreight.id === routeFreight.freightId,
          );

          return {
            ...routeFreight,
            isDeleted: relatedOrderFreight?.isDeleted || routeFreight.isDeleted,
          };
        }),
      };
    });
  }

  private calculateTravelOrderIds(route: TripRoute, routeStop: TripRouteStop): (string | number)[] {
    const freightIds = this.routeFreights
      .filter((routeFreight) => routeStop.freights.includes(routeFreight.id))
      .map((routeFreight) => routeFreight.freightId);

    const relatedOrderIds = this.orders
      .filter((order) => order.freights.some((freight) => freight.id && freightIds.includes(freight.id)))
      .map((order) => order.id);

    const relatedTravelOrders = this.routes
      .filter((currentRoute) => {
        return isSameEntity<TripRoute>(currentRoute, route);
      })
      .flatMap((currentRoute) => currentRoute.travelOrders)
      .filter((travelOrder) => !!travelOrder.tempId && relatedOrderIds.includes(travelOrder.orderId));

    return relatedTravelOrders.map((travelOrder) => travelOrder.tempId!);
  }

  private setTravelOrderIdsForRouteStops() {
    this.trip.routes = this.trip.routes.map((route) => ({
      ...route,
      routeStops: route.routeStops.map((routeStop) => ({
        ...routeStop,
        travelOrderIds: this.calculateTravelOrderIds(route, routeStop),
      })),
    }));
  }

  private setTravelOrderIdsForRouteStop(route: TripRoute, routeStop: TripRouteStop) {
    this.trip.routes = this.trip.routes.map((currentRoute) => {
      if (currentRoute.id !== route.id) {
        return currentRoute;
      }

      return {
        ...currentRoute,
        routeStops: currentRoute.routeStops.map((currentRouteStop) => {
          if (currentRouteStop.id === routeStop.id) {
            return {
              ...currentRouteStop,
              travelOrderIds: this.calculateTravelOrderIds(currentRoute, currentRouteStop),
            };
          }
          return currentRouteStop;
        }),
      };
    });
  }

  private async loadNewTrip(): Promise<void> {
    if (!this.route.query.order || !this.route.query.truck) {
      throw new Error("Missing order or truck for new trip");
    }
    await this.loadOrder(this.route.query!.order.toString());
    await this.loadTruck(parseInt(this.route.query!.truck.toString()));
  }

  private async loadExistingTrip(): Promise<void> {
    if (!this.route.params.id) {
      throw new Error("Missing trip id");
    }
    await this.loadTrip(parseInt(this.route.params.id.toString()));
  }

  private async loadOrder(orderId: string): Promise<void> {
    let order: TripOrder | undefined;
    const resp = await this.api.orders.findOrderById(orderId);
    if (resp.success && resp.data) {
      order = {
        ...resp.data,
        files: [],
        tripStops: resp.data.orderStops,
        freights: resp.data!.freights.map((f, i) => ({ ...f, number: i + 1 })),
      };
    }
    if (!order) {
      throw new Error("Order not found");
    }

    const fileResp = await this.api.orders.findOrderFiles(order.id!);
    if (fileResp.success) {
      order.files = fileResp.data!;
    }

    this.addOrder(order);
  }

  private async loadTruck(truckId: number): Promise<void> {
    const resp = await this.api.trucks.findTruckById(truckId);
    if (resp.success) {
      this.addRoute(resp.data!);
      this.setTravelOrderIdsForRouteStops();
    } else {
      throw new Error("Truck not found");
    }
  }

  private createInitialRouteStopFreights(tripStop: TripStop, copyFreights: boolean): string[] {
    if (!copyFreights) {
      return [];
    }

    if (isReloadStop(tripStop)) {
      return tripStop.reloadFreights.filter((freight) => freight.id).flatMap((f) => f.id!) || [];
    }

    return tripStop.freights || [];
  }

  private getInitialRouteStopPosition(tripStop: TripStop): number {
    if (!this.routeStops.length && tripStop.position) {
      return tripStop.position;
    }

    const previousTripStop = this.tripStops.find(
      (item) => tripStop.position !== undefined && tripStop.position - 1 === item.position,
    );

    const previousRouteStop = this.routeStops.find((routeStop) => {
      if (previousTripStop?.isReload) {
        return routeStop.reloadStopId === previousTripStop.id && routeStop.type === OrderStopType.pickup;
      }

      return routeStop.orderStopId === previousTripStop?.id;
    });

    return previousRouteStop?.position ? previousRouteStop.position + 1 : 1;
  }

  private buildRouteStopFromOrderStop(
    tripStop: TripStop,
    copyFreights = true,
    useStop = true,
    status: OrderStopStatus | null = null,
  ): TripRouteStop {
    const routeStop: TripRouteStop = {
      bolFiles: [],
      checkInTime: null,
      checkOutTime: null,
      loadUnloadTime: null,
      name: "",
      number: tripStop.number || 0,
      oldFacilityId: null,
      otherFiles: [],
      podFiles: [],
      podSignedBy: null,
      position: this.getInitialRouteStopPosition(tripStop),
      state: RouteStopState.normal,
      verifyDispatcherId: null,
      verifyTime: null,
      reloadStopId: tripStop.isReload ? tripStop.id : undefined,
      orderStopId: tripStop.isReload ? null : tripStop.id || null,
      order: !isReloadStop(tripStop)
        ? {
            id: tripStop.order!.id!,
            number: tripStop.order!.number!,
            status: tripStop.order!.status!,
            rate: tripStop.order?.rate || 0,
          }
        : null,
      type: tripStop.type,
      facility: tripStop.facility,
      facilityId: tripStop.facilityId,
      freights: this.createInitialRouteStopFreights(tripStop, copyFreights),
      editable: this.trip.routes.length > 1,
      isReload: tripStop.isReload || false,
      use: useStop,
      status: status || tripStop.status,
      note: tripStop.note,
      timezone: tripStop.timezone,
      timeType: tripStop.timeType,
      timeFrom: tripStop.timeFrom,
      timeTo: tripStop.timeTo,
      time2Type: tripStop.time2Type,
      time2From: tripStop.time2From,
      time2To: tripStop.time2To,
      dispatchedTimeType: tripStop.timeType,
      dispatchedTimeFrom: tripStop.timeFrom,
      dispatchedTimeTo: tripStop.timeTo,
      tempId: this.generateId(),
    };
    this.generateStopName(routeStop);
    return routeStop;
  }

  private updateTripStopsPositionAndNumber(newTripStop: TripStop) {
    this.tripStops.forEach((tripStop) => {
      if (!newTripStop.position || !tripStop.position) {
        return;
      }
      if (tripStop.position < newTripStop.position) {
        return;
      }

      tripStop.position++;

      const bothReloads = newTripStop.isReload && tripStop.isReload;
      const bothRegular = !newTripStop.isReload && !tripStop.isReload;

      if ((bothReloads || bothRegular) && tripStop.number) {
        tripStop.number++;
      }
    });
  }

  private updateRouteStopsPositionAndNumber(route: TripRoute, newStop: TripRouteStop) {
    route.routeStops.forEach((routeStop) => {
      if (routeStop.position < newStop.position) {
        return;
      }

      routeStop.position++;

      if (newStop.isReload === routeStop.isReload && newStop.type === routeStop.type && routeStop.number) {
        routeStop.number++;
      }
    });
  }

  private generateStopName<T>(
    stop: T & {
      name?: string;
      type?: string;
      isReload?: boolean;
      number?: number;
    },
  ): T {
    stop.name = stop.type === OrderStopType.pickup ? "Pick up" : "Delivery";
    if (stop.number) {
      stop.name += " #" + stop.number;
    }

    return stop;
  }

  private numerateStops<
    T extends {
      type: OrderStopType;
      number?: number;
      isReload?: boolean;
      position: number;
    },
  >(stops: T[]): T[] {
    let deliveryNum = 1;
    let pickupNum = 1;

    return stops
      .sort((a, b) => a.position - b.position)
      .map((stop) => {
        if (stop.isReload) {
          return stop;
        }

        if (stop.type === OrderStopType.delivery) {
          stop.number = deliveryNum++;
        }
        if (stop.type === OrderStopType.pickup) {
          stop.number = pickupNum++;
        }

        return stop;
      });
  }

  private numerateOrderStops(): void {
    let position = 1;
    let deliveryNum = 1;
    let pickupNum = 1;

    for (const orderStop of this.trip.orders.flatMap((o) => o.tripStops)) {
      orderStop.position = position;
      position++;

      if (orderStop.type === OrderStopType.delivery) {
        orderStop.number = deliveryNum;
        deliveryNum++;
      }
      if (orderStop.type === OrderStopType.pickup) {
        orderStop.number = pickupNum;
        pickupNum++;
      }

      this.generateStopName(orderStop);
    }
  }

  private numerateStandardRouteStop() {
    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        routeStops: this.numerateStops<TripRouteStop>(route.routeStops),
      };
    });
  }

  private numerateReloadStops() {
    let deliveryNum = 0;
    let pickupNum = 0;

    this.trip.routes = this.trip.routes.map((route) => {
      return {
        ...route,
        routeStops: route.routeStops.map((routeStop) => {
          if (routeStop.isReload && routeStop.type == OrderStopType.delivery) {
            deliveryNum++;
            return {
              ...routeStop,
              number: deliveryNum,
              name: `Delivery #${deliveryNum}`,
            };
          }

          if (routeStop.isReload && routeStop.type === OrderStopType.pickup) {
            pickupNum++;
            return {
              ...routeStop,
              number: pickupNum,
              name: `Pick up #${pickupNum}`,
            };
          }

          return routeStop;
        }),
      };
    });
  }

  private getTravelOrderFromOrder(order: TripOrder, routeIndex: number): TravelOrderDto {
    return {
      order: {
        ...order,
        id: order.id!,
        number: order.number!,
        status: order.status!,
        orderStops: order.tripStops,
      },
      orderId: order.id!,
      status: TravelOrderStatus.notStarted,
      updateInterval: null,
      lastUpdateTime: null,
      nextUpdateTime: null,
      position: routeIndex + 1,
      name: createTOName(routeIndex, order.number),
      tempId: this.generateId(),
    };
  }

  private generateId() {
    const nanoid = customAlphabet("1234567890abcdef", 16);
    return nanoid();
  }

  private recalculateRouteStops(route: TripRoute) {
    let position = 1,
      pickUpNumber = 1,
      deliveryNumber = 1;
    for (const stop of route.routeStops) {
      if (stop.type === OrderStopType.pickup) {
        stop.number = pickUpNumber++;
      } else if (stop.type === OrderStopType.delivery) {
        stop.number = deliveryNumber++;
      }

      stop.position = position++;
      this.generateStopName(stop);
    }
  }

  private isTravelOrderCancelled(travelOrder: TravelOrderDto): boolean {
    return TravelOrderCancellationStatusMap.some((s) => s.id === travelOrder.status);
  }

  private createOrderFacilityFromRouteFacility(
    routeFacility: RouteStopFacilityDto | undefined,
  ): OrderFacilityDto | undefined {
    if (!routeFacility) {
      return undefined;
    }

    return {
      id: routeFacility.id,
      name: routeFacility.name,
      type: routeFacility.type,
      timezone: routeFacility.timezone,
      addressLine: routeFacility.addressLine,
      address: routeFacility.address,
      addressCoordinates: routeFacility.addressCoordinates,
      preciseCoordinates: null,
    };
  }

  private createTripReloadStopBasedOnRouteStops() {
    const groupedByPosition = this.getReloadRouteStopsByPosition(this.routes);
    let globalReloadStopNumber = 0;

    Object.entries(groupedByPosition).forEach(([positionKey, stops]) => {
      const position = Number(positionKey);
      globalReloadStopNumber++;

      // Group all related reloadFreights
      const freights: TripRouteFreight[] = [];
      stops.forEach((routeStop) => {
        const matchedFreights = this.routeFreights.filter((rf) => routeStop.freights.includes(rf.id));
        matchedFreights.forEach((freight) => {
          freights.push(freight);
        });
      });

      // Define status of trip reload
      const statuses = stops.map((s) => s.status);
      let status: OrderStopStatus = OrderStopStatus.notStarted;

      if (statuses.includes(OrderStopStatus.inProgress)) {
        status = OrderStopStatus.inProgress;
      } else if (statuses.includes(OrderStopStatus.notStarted)) {
        status = OrderStopStatus.notStarted;
      } else {
        status = OrderStopStatus.completed;
      }

      const reloadFreights = freights.map((freight, i) => ({
        ...freight,
        number: i + 1,
        name: getFreightName(freight, i + 1, "", false, true),
      }));

      const sampleStop = stops[0];

      const relatedOrder = this.tripStops.find((os) => os.position === position)?.order;

      const tripReloadStop: TripReloadStop = {
        id: sampleStop.reloadStopId || "",
        type: OrderStopType.reload,
        status,
        isReload: true,
        name: `Reload stop #${globalReloadStopNumber}`,
        timeType: sampleStop.timeType,
        timeTo: sampleStop.timeTo,
        position,
        facility: this.createOrderFacilityFromRouteFacility(sampleStop.facility),
        facilityId: sampleStop.facilityId,
        timezone: sampleStop.timezone,
        note: sampleStop.note,
        number: globalReloadStopNumber,
        reloadFreights,
        isDeleted: Boolean(sampleStop.isDeleted),
        state: sampleStop.state,
      };

      this.trip = {
        ...this.trip,
        orders: this.trip.orders.map((order) => {
          const updatedOrderStops = order.tripStops.map((orderStop) => {
            if (orderStop.position && tripReloadStop.position && orderStop.position >= tripReloadStop.position) {
              return { ...orderStop, position: orderStop.position + 1 };
            }
            return orderStop;
          });

          if (!relatedOrder || order.id !== relatedOrder.id) {
            return {
              ...order,
              tripStops: [...updatedOrderStops],
            };
          }

          return {
            ...order,
            tripStops: [...updatedOrderStops, tripReloadStop],
          };
        }),
      };
    });
  }

  private getMissingRouteStopsByRoute(): Record<number, MissingStopsByRoute> | null {
    const missingStopsByRoute: Record<number, MissingStopsByRoute> = {};

    for (const route of this.routes) {
      const routeOrderStopIds = new Set(
        route.routeStops
          .map((routeStop) => (routeStop.isReload ? routeStop.reloadStopId : routeStop.orderStopId))
          .filter(Boolean),
      );

      const standardStops: TripOrderStop[] = [];
      const reloadStops: MissingReloadStopInfo[] = [];

      for (const orderStop of this.tripStops) {
        if (isReloadStop(orderStop)) {
          const relatedStops = route.routeStops.filter((rs) => rs.isReload && rs.reloadStopId === orderStop.id);

          const hasPickup = relatedStops.some((rs) => rs.type === OrderStopType.pickup);
          const hasDelivery = relatedStops.some((rs) => rs.type === OrderStopType.delivery);

          const missingTypes: OrderStopType[] = [];
          if (!hasPickup) missingTypes.push(OrderStopType.pickup);
          if (!hasDelivery) missingTypes.push(OrderStopType.delivery);

          if (missingTypes.length) {
            reloadStops.push({ stop: orderStop, missingTypes });
          }
        } else {
          if (!routeOrderStopIds.has(orderStop.id)) {
            standardStops.push(orderStop);
          }
        }
      }

      if (standardStops.length || reloadStops.length) {
        missingStopsByRoute[route.id!] = {
          standard: standardStops,
          reload: reloadStops,
        };
      }
    }

    return Object.keys(missingStopsByRoute).length ? missingStopsByRoute : null;
  }

  private addEmptyRouteStops() {
    const missingStops = this.getMissingRouteStopsByRoute();

    if (!missingStops) {
      return;
    }

    this.trip.routes = this.routes.map((route) => {
      const missingOrderStops = missingStops[route.id!];

      const missingStandardRouteStops = missingOrderStops.standard.map((orderStop) => {
        return this.buildRouteStopFromOrderStop(orderStop, true, false);
      });

      const missingReloadRouteStops = missingOrderStops.reload.flatMap((missingInfo) => {
        return missingInfo.missingTypes.map((type) => {
          const reloadRouteStop = this.buildRouteStopFromOrderStop(
            {
              ...missingInfo.stop,
              type,
            },
            false,
            false,
          );

          if (type === OrderStopType.pickup) {
            reloadRouteStop.position++;
          }

          return reloadRouteStop;
        });
      });

      return {
        ...route,
        routeStops: [...route.routeStops, ...missingStandardRouteStops, ...missingReloadRouteStops],
      };
    });
  }

  private sortOrderStops(tripStops: TripOrderStop[], routeStops: TripRouteStop[]): TripOrderStop[] {
    // Create a map of orderStopId to routeStop for quick lookup
    const routeStopMap: Record<string, TripRouteStop> = {};
    for (const routeStop of routeStops) {
      routeStopMap[routeStop.orderStopId!] = routeStop;
    }

    // Separate order stops into two lists: those with and without corresponding route stops
    const withRouteStops: TripOrderStop[] = [];
    const withoutRouteStops: TripOrderStop[] = [];

    for (const orderStop of tripStops) {
      if (routeStopMap[orderStop.id!]) {
        withRouteStops.push(orderStop);
      } else {
        withoutRouteStops.push(orderStop);
      }
    }

    // Sort the order stops with route stops by their corresponding route stop positions
    withRouteStops.sort((a, b) => {
      return routeStopMap[a.id!].position - routeStopMap[b.id!].position;
    });

    // Merge the two lists, inserting order stops without route stops in their original order
    const sortedOrderStops: TripOrderStop[] = [];
    let positionCounter = 1;

    for (const orderStop of tripStops) {
      if (routeStopMap[orderStop.id!]) {
        // Add the order stop with the route stop, assigning the correct position
        const sortedStop = withRouteStops.shift()!;
        sortedStop.position = positionCounter++;
        sortedOrderStops.push(sortedStop);
      } else {
        // Add the order stop without the route stop, assigning the correct position
        const unlinkedStop = withoutRouteStops.shift()!;
        unlinkedStop.position = positionCounter++;
        sortedOrderStops.push(unlinkedStop);
      }
    }

    return sortedOrderStops;
  }
}
